import React from 'react';

/**
 * ComplaintWorkflowDiagram — v3 (Feb-2026) — professional 4-lane redesign.
 *
 * Previous version was 5 lanes wide, causing the STATUS lane to be clipped
 * off the viewport. This rewrite:
 *   • drops the separate STATUS lane and puts the status pill INSIDE each
 *     card (bottom-strip badge), so the whole diagram fits in ~1000px
 *   • uses generous card height + larger typography (13/12px) for readability
 *   • cleaner, shorter S-curve connectors
 *   • branch tags (A/B) sit on the card border, not as absolute-positioned
 *     dots — no more visual clutter
 *   • dark-mode friendly via a soft neutral palette
 */
export default function ComplaintWorkflowDiagram() {
  // Fits inside max-w-4xl (896px) parent — 4 lanes × 194 + gaps → 844px total.
  const laneWidth = 194;
  const laneGap = 12;
  const padX = 16;
  const laneX = (i) => padX + i * (laneWidth + laneGap);

  const lanes = [
    { key: 'tenant',      label: 'TENANT',           color: '#7c3aed', bg: '#faf5ff' },
    { key: 'engineer',    label: 'ENGINEER / ADMIN', color: '#d97706', bg: '#fffbeb' },
    { key: 'technician',  label: 'TECHNICIAN',       color: '#0d9488', bg: '#f0fdfa' },
    { key: 'storekeeper', label: 'STOREKEEPER',      color: '#2563eb', bg: '#eff6ff' },
  ];
  const laneOf = (key) => lanes.findIndex(l => l.key === key);

  // Row 108px lets us stack: title/note (≈ 70px, allows 2-line wrap) + status ribbon (≈ 22px).
  const rowH = 108;
  const y0 = 112;

  const steps = [
    { lane: 'tenant',      icon: '📝', title: 'Raises complaint',       note: 'Selects building & unit',           status: 'OPEN',                statusColor: '#f59e0b' },
    { lane: 'engineer',    icon: '👤', title: 'Assigns technician',     note: 'Creates linked Work Order',         status: 'ASSIGNED',            statusColor: '#3b82f6' },
    { lane: 'technician',  icon: '🔧', title: 'Visits & attends',       note: 'Diagnoses the fault on site',       status: 'IN PROGRESS',         statusColor: '#8b5cf6' },
    { lane: 'technician',  icon: '📸', title: 'Completes work',         note: 'Photos + tenant confirmation',      status: 'RESOLVED',            statusColor: '#10b981', branch: 'A' },
    { lane: 'technician',  icon: '📦', title: 'Requests material',      note: 'Qty · Brand · Size · Returnable?',  status: 'MATERIALS PENDING',   statusColor: '#f97316', branch: 'B' },
    { lane: 'engineer',    icon: '✅', title: 'Approves material',      note: 'Reviews & flags returnables',       status: 'MATERIALS APPROVED', statusColor: '#22c55e', branch: 'B' },
    { lane: 'storekeeper', icon: '🏬', title: 'Issues from store',      note: 'Stock auto-decrements',             status: 'MATERIALS ISSUED',   statusColor: '#0284c7', branch: 'B' },
    { lane: 'technician',  icon: '🛠️', title: 'Collects & completes',   note: 'Returns spares to store',           status: 'RESOLVED',            statusColor: '#10b981', branch: 'B' },
    { lane: 'tenant',      icon: '✍️', title: 'Tenant signs off',       note: 'In-app signature captured',         status: 'RESOLVED',            statusColor: '#10b981' },
    { lane: 'engineer',    icon: '🔒', title: 'Engineer verifies & closes', note: 'Only Engineer / Manager / Admin', status: 'CLOSED',           statusColor: '#334155' },
  ];

  const svgWidth = padX * 2 + lanes.length * laneWidth + (lanes.length - 1) * laneGap;
  const totalHeight = y0 + steps.length * rowH + 60;

  return (
    <div className="w-full overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
      <svg
        viewBox={`0 0 ${svgWidth} ${totalHeight}`}
        width="100%"
        preserveAspectRatio="xMidYMin meet"
        className="min-w-[820px]"
        role="img"
        aria-label="Complaint lifecycle swimlane"
      >
        {/* Title band */}
        <text x={svgWidth / 2} y="30" textAnchor="middle" fontSize="17" fontWeight="700" fill="#0f172a">
          Complaint Lifecycle — who acts, and how the status moves
        </text>
        <text x={svgWidth / 2} y="54" textAnchor="middle" fontSize="12" fill="#64748b">
          <tspan fill="#059669" fontWeight="700">Branch A</tspan> · No material required
          <tspan dx="18" fill="#c2410c" fontWeight="700">Branch B</tspan> · Material required
        </text>

        {/* Lane columns */}
        {lanes.map((l, i) => (
          <g key={l.key}>
            <rect
              x={laneX(i)} y="72"
              width={laneWidth} height={totalHeight - 108}
              rx="14"
              fill={l.bg}
              stroke={l.color}
              strokeOpacity="0.28"
            />
            <rect x={laneX(i)} y="72" width={laneWidth} height="32" rx="14" fill={l.color} />
            <rect x={laneX(i)} y="88" width={laneWidth} height="16" fill={l.color} />
            <text x={laneX(i) + laneWidth / 2} y="93" textAnchor="middle" fontSize="12" fontWeight="700" fill="white" letterSpacing="1.4">
              {l.label}
            </text>
          </g>
        ))}

        {/* Connectors first (so they draw beneath cards) */}
        {steps.map((s, idx) => {
          if (idx === 0) return null;
          const prev = steps[idx - 1];
          const prevLi = laneOf(prev.lane);
          const li = laneOf(s.lane);
          const sx = laneX(prevLi) + laneWidth / 2;
          const sy = y0 + (idx - 1) * rowH + (rowH - 16);
          const ex = laneX(li) + laneWidth / 2;
          const ey = y0 + idx * rowH + 4;
          const midY = (sy + ey) / 2;
          const branchColor = s.branch === 'B' ? '#f97316' : s.branch === 'A' ? '#10b981' : '#94a3b8';
          return (
            <g key={`c-${idx}`} stroke={branchColor} strokeWidth="1.8" fill="none" opacity="0.55">
              <path d={`M ${sx} ${sy} C ${sx} ${midY}, ${ex} ${midY}, ${ex} ${ey}`} />
              <polygon points={`${ex - 5},${ey - 7} ${ex + 5},${ey - 7} ${ex},${ey}`} fill={branchColor} stroke="none" opacity="1" />
            </g>
          );
        })}

        {/* Step cards */}
        {steps.map((s, idx) => {
          const li = laneOf(s.lane);
          const y = y0 + idx * rowH;
          const cardX = laneX(li) + 10;
          const cardW = laneWidth - 20;
          const cardH = rowH - 16;
          const bodyH = cardH - 26; // reserve 26 for status ribbon
          return (
            <g key={`s-${idx}`}>
              {/* Card body */}
              <foreignObject x={cardX} y={y} width={cardW} height={cardH}>
                <div
                  xmlns="http://www.w3.org/1999/xhtml"
                  style={{
                    boxSizing: 'border-box',
                    height: '100%',
                    background: 'white',
                    border: `1.5px solid ${lanes[li].color}`,
                    borderRadius: 10,
                    fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                    boxShadow: '0 1px 2px rgba(15,23,42,.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {/* Branch tag */}
                  {s.branch && (
                    <span style={{
                      position: 'absolute', top: 6, right: 6,
                      fontSize: 10, fontWeight: 800,
                      color: s.branch === 'A' ? '#059669' : '#c2410c',
                      background: s.branch === 'A' ? '#ecfdf5' : '#fff7ed',
                      borderRadius: 999, padding: '2px 7px',
                      border: `1px solid ${s.branch === 'A' ? '#a7f3d0' : '#fed7aa'}`,
                      lineHeight: 1.1,
                    }}>{s.branch}</span>
                  )}
                  {/* Body */}
                  <div style={{ padding: '10px 12px 6px', flex: 1, minHeight: bodyH, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 22, height: 22, borderRadius: 999,
                        background: lanes[li].color, color: 'white',
                        fontSize: 11, fontWeight: 800, flexShrink: 0,
                      }}>{idx + 1}</span>
                      <span style={{ fontSize: 16, lineHeight: 1 }}>{s.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>{s.title}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, lineHeight: 1.3, paddingLeft: 30 }}>{s.note}</div>
                  </div>
                  {/* Status ribbon */}
                  <div style={{
                    background: s.statusColor,
                    color: 'white',
                    textAlign: 'center',
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: 0.8,
                    padding: '5px 6px',
                    lineHeight: 1,
                  }}>{s.status}</div>
                </div>
              </foreignObject>
            </g>
          );
        })}

        {/* Decision-point marker between step 3 and 4/5 */}
        <foreignObject x={laneX(laneOf('technician'))} y={y0 + 3 * rowH - 20} width={laneWidth} height="18">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{
            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
            fontSize: 10.5,
            fontStyle: 'italic',
            color: '#475569',
            background: 'rgba(255,255,255,.9)',
            border: '1px dashed #cbd5e1',
            borderRadius: 999,
            padding: '2px 8px',
            display: 'inline-block',
            textAlign: 'center',
            width: 'fit-content',
            margin: '0 auto',
          }}>◆ decision: material needed?</div>
        </foreignObject>

        {/* Footer tip */}
        <foreignObject x={padX} y={totalHeight - 46} width={svgWidth - padX * 2} height="34">
          <div xmlns="http://www.w3.org/1999/xhtml" style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif', fontSize: 12, color: '#475569', lineHeight: 1.4 }}>
            <strong style={{ color: '#0f172a' }}>Tip:</strong> Only <strong style={{ color: '#334155' }}>Engineer / Manager / Admin</strong> can close a complaint. Technicians can advance up to <strong style={{ color: '#10b981' }}>RESOLVED</strong>. Returnable items sit on the <strong style={{ color: '#0891b2' }}>Pending Returns</strong> queue until the storekeeper receives them back.
          </div>
        </foreignObject>
      </svg>

      {/* Compact text summary below the diagram — mobile-first fallback */}
      <div className="p-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <div className="p-3 rounded-lg bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-800">
            <div className="font-semibold text-emerald-800 dark:text-emerald-300 mb-1">Branch A · No material required</div>
            <ol className="list-decimal ml-4 space-y-0.5 text-slate-700 dark:text-slate-300">
              <li>Tenant raises complaint → <strong className="text-amber-600">OPEN</strong></li>
              <li>Engineer assigns tech + WO → <strong className="text-blue-600">ASSIGNED</strong></li>
              <li>Technician attends → <strong className="text-violet-600">IN PROGRESS</strong></li>
              <li>Photos + tenant confirms → <strong className="text-emerald-600">RESOLVED</strong></li>
              <li>Engineer verifies & closes → <strong className="text-slate-700 dark:text-slate-300">CLOSED</strong></li>
            </ol>
          </div>
          <div className="p-3 rounded-lg bg-white dark:bg-slate-900 border border-orange-200 dark:border-orange-800">
            <div className="font-semibold text-orange-800 dark:text-orange-300 mb-1">Branch B · Material required</div>
            <ol className="list-decimal ml-4 space-y-0.5 text-slate-700 dark:text-slate-300">
              <li>Technician requests material → <strong className="text-orange-600">MATERIALS PENDING</strong></li>
              <li>Engineer approves (returnable flag) → <strong className="text-green-600">MATERIALS APPROVED</strong></li>
              <li>Storekeeper issues from store → <strong className="text-sky-600">MATERIALS ISSUED</strong></li>
              <li>Technician completes, returns spares → <strong className="text-emerald-600">RESOLVED</strong></li>
              <li>Engineer verifies & closes → <strong className="text-slate-700 dark:text-slate-300">CLOSED</strong></li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
